// SearchForPlayerPicker.js
import React, { useEffect, useMemo, useState } from 'react'
import './SelectLeague.css' // keeps your searchInput class styling

const SearchForPlayerPicker = ({
	label,
	placeholder = 'Enter player name to search',
	onSelectPlayer,
	disableSelfId, // optional: prevent selecting the same player in both boxes
}) => {
	const [isLoading, setIsLoading] = useState(false)
	const [error, setError] = useState(null)
	const [loadedAllPlayers, setLoadedAllPlayers] = useState([])
	const [searchTerm, setSearchTerm] = useState('')
	const [filteredResults, setFilteredResults] = useState([])
	const [isOpen, setIsOpen] = useState(false)

	// Load all players once
	useEffect(() => {
		const sendRequest = async () => {
			setIsLoading(true)
			try {
				const response = await fetch(
					`${process.env.REACT_APP_BACKEND_URL}/admin/getAllPlayers`,
				)
				const responseData = await response.json()
				if (!response.ok) throw new Error(responseData.message)

				setLoadedAllPlayers(responseData.allPlayers || [])
			} catch (err) {
				setError(err.message)
			} finally {
				setIsLoading(false)
			}
		}
		sendRequest()
	}, [])

	// Build sorted display array
	const playerArray = useMemo(() => {
		if (!loadedAllPlayers || loadedAllPlayers.length === 0) return []

		const sorted = [...loadedAllPlayers].sort(
			(a, b) =>
				a.lastName.localeCompare(b.lastName) ||
				a.firstName.localeCompare(b.firstName),
		)

		return sorted.map((p) => ({
			player: p,
			label: `${p.lastName}, ${p.firstName} | ${p.usabh || ''}`,
		}))
	}, [loadedAllPlayers])

	// Filter results
	useEffect(() => {
		if (!isOpen) {
			setFilteredResults([])
			return
		}

		if (!searchTerm || searchTerm.trim() === '') {
			setFilteredResults([])
			return
		}
		const q = searchTerm.toLowerCase()
		const results = playerArray
			.filter((x) => x.label.toLowerCase().includes(q))
			.slice(0, 30)

		setFilteredResults(results)
	}, [searchTerm, playerArray, isOpen])

	const handlePick = (picked) => {
		if (!picked?.player) return
		if (disableSelfId && String(picked.player._id) === String(disableSelfId))
			return

		onSelectPlayer && onSelectPlayer(picked.player)

		// CLOSE DROPDOWN (this is the key)
		setIsOpen(false)

		setSearchTerm(picked.label) // show what they picked in the input
		setFilteredResults([])
	}

	return (
		<div style={{ marginBottom: '1.25rem' }}>
			{label && <h4 style={{ marginBottom: '.5rem' }}>{label}</h4>}

			<div style={{ position: 'relative', width: '100%' }}>
				<input
					className='searchInput'
					type='text'
					placeholder={placeholder}
					value={searchTerm}
					onFocus={() => setIsOpen(true)}
					onChange={(e) => {
						setSearchTerm(e.target.value)
						setIsOpen(true)
					}}
					onBlur={() => {
						// tiny delay so click on a result still registers
						setTimeout(() => setIsOpen(false), 120)
					}}
				/>

				{/* dropdown */}
				{isOpen && searchTerm.length > 3 && filteredResults.length > 0 && (
					<ul
						className='playerDropdown'
						style={{
							position: 'absolute',
							top: '100%',
							left: 0,
							right: 0,
							zIndex: 9999,
							marginTop: '.25rem',
							paddingLeft: 0,
							listStyle: 'none',
							background: 'white',
							border: '1px solid #ddd',
							borderRadius: '10px',
							boxShadow: '0 8px 20px rgba(0,0,0,0.12)',
							maxHeight: '260px',
							overflowY: 'auto',
						}}
					>
						{filteredResults.map((item) => (
							<li
								key={item.player._id}
								className='playerSearchResult'
								onMouseDown={() => handlePick(item)}
								style={{
									textAlign: 'center',
									listStyleType: 'none',
									cursor: 'default', // arrow cursor
								}}
								onMouseEnter={(e) =>
									(e.currentTarget.style.background = '#f7f7f7')
								}
								onMouseLeave={(e) =>
									(e.currentTarget.style.background = 'transparent')
								}
							>
								{item.label}
							</li>
						))}
					</ul>
				)}
			</div>

			{searchTerm.length > 2 && filteredResults.length === 0 && !isLoading && (
				<p style={{ textAlign: 'center' }}>No results found.</p>
			)}

			{isLoading && <p style={{ textAlign: 'center' }}>Loading players...</p>}
			{error && <p style={{ textAlign: 'center', color: 'red' }}>{error}</p>}
		</div>
	)
}

export default SearchForPlayerPicker
